"use client";
import React from 'react'
import { Card, CardContent } from "@/components/ui/custom/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/custom/ui/avatar";
import { Badge } from "@/components/ui/custom/ui/badge";
import { Button } from "@/components/ui/custom/ui/button";
import { Separator } from "@/components/ui/custom/ui/separator";

import {
  Info,
  Wallet,
  Coins,
  Gem,
  BarChart3,
  Award,
  LogIn,
  MonitorPlay,
  Users,
  Package,
  Network,
  Layers,
  ZapIcon,
} from "lucide-react"

const statGroups = [
  {
    title: "Tokens & Assets",
    items: [
      { key: "mxs", label: "MXS", icon: Coins, color: "text-yellow-400" },
      { key: "gold", label: "Gold", icon: Coins, color: "text-yellow-500" },
      { key: "ruby", label: "Ruby", icon: Gem, color: "text-red-500" },
      { key: "sapphire", label: "Sapphire", icon: Gem, color: "text-blue-500" },
      { key: "xb", label: "XB", icon: Coins, color: "text-green-400" },
    ],
  },
  {
    title: "Activity",
    items: [
      { key: "level_count", label: "Levels", icon: BarChart3, color: "text-purple-400" },
      { key: "missions_count", label: "Missions", icon: Award, color: "text-orange-400" },
      { key: "login_count", label: "Logins", icon: LogIn, color: "text-blue-400" },
      { key: "ads_count", label: "Ads Viewed", icon: MonitorPlay, color: "text-gray-400" },
    ],
  },
  {
    title: "Referrals",
    items: [
      { key: "user_referral_count", label: "Users", icon: Users, color: "text-green-400" },
      { key: "products_referral", label: "Products", icon: Package, color: "text-yellow-400" },
      { key: "node_referral_count", label: "Nodes", icon: Network, color: "text-blue-400" },
    ],
  },
  {
    title: "NFTs",
    items: [
      { key: "nft_count", label: "Count", icon: Layers, color: "text-purple-400" },
      { key: "nft_power", label: "Power", icon: ZapIcon, color: "text-yellow-400" },
    ],
  },
]

const ProfileCard = ({ user, walletAddress, isEligible, claimButtonLoading, status, currentUserResult, handleClaim }) => {
  return (
    <Card className="bg-gradient-to-r from-metaxseed-blue to-blue-500 mb-6 border-0 rounded-md">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* User Profile Section */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 justify-between">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative">
                <Avatar className="h-20 w-20 border-2 border-[#fe5000]">
                  <AvatarImage src={user.photo_url} className="object-cover" />
                  <AvatarFallback className="bg-[#003087] text-white text-2xl">
                    {user.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -top-2 -right-2 bg-[#fe5000] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  #{10}
                </div>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold">{user.name}</h3>
                <div className="flex items-center justify-center sm:justify-start gap-1 text-gray-300 mt-1">
                  <Wallet className="h-4 w-4" />
                  <span>{walletAddress.slice(0, 5)}...{walletAddress.slice(-5)}</span>
                </div>
                {isEligible ? (
                  <Badge className="bg-green-500 mt-1">Eligible for Airdrop</Badge>
                ) : (
                  <Badge className="bg-red-500 mt-1">Not Eligible</Badge>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center mt-4">
              <Button className="bg-gradient-to-r from-orange-500 to-yellow-500 gothamMedium h-12 w-[150px]"
                disabled={!isEligible || claimButtonLoading}
                onClick={handleClaim}>
                {claimButtonLoading ? (
                  <span className="ml-2">Loading...</span>
                ) : (
                  <span>{status || "Claim"}</span>
                )}
              </Button>
            </div>
          </div>

          <Separator className="bg-gray-700" />

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-3">
                <h3 className="font-semibold text-[#fe5000] text-lg">{group.title}</h3>
                <div className="space-y-2">
                  {group.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className="flex items-center justify-between p-2 rounded-md bg-white bg-opacity-20 backdrop-blur-md shadow-lg"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md bg-metaxseed-blue ${item.color}`}>
                          <item.icon className="h-4 w-4" />
                        </div>
                        <span className="text-sm">{item.label}</span>
                      </div>
                      {currentUserResult && (
                        <div className="font-mono font-medium">
                          {currentUserResult[item.key]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default ProfileCard
